<template>
  <div class="flex flex-col h-full">
    <!-- ── Header: back (context-aware) + close ── -->
    <div class="flex items-center justify-between pb-2 px-1 border-b border-border shrink-0">
      <button
        type="button"
        class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
        aria-label="Back"
        @click="handleBack"
      >
        <Icon name="arrow-left" :size="20" weight="regular" />
      </button>
      <button
        type="button"
        class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
        aria-label="Close"
        @click="emit('close')"
      >
        <Icon name="x" :size="20" weight="regular" />
      </button>
    </div>

    <!-- ── Step 1: Mobile Number ── -->
    <template v-if="step === 'mobile'">
      <div class="flex-1 overflow-y-auto px-5 py-6">
        <Logo class="h-3 mb-6" />
        <h3 class="text-[24px] font-bold text-text-primary mb-6">Enter Mobile Number</h3>
        <MobileInput
          v-model="mobileNo"
          label="Enter Your Mobile Number"
          placeholder="Enter mobile number"
          :error="mobileError"
          :disabled="loading"
        />
        <p class="text-sm text-text-secondary mt-2">
          We'll send you a verification code to place your order
        </p>
      </div>
      <div class="px-5 pb-6 shrink-0">
        <Button
          variant="primary"
          size="large"
          full-width
          :loading="loading"
          :disabled="!isMobileValid"
          @click="handleSendOtp"
        >
          Next
        </Button>
      </div>
    </template>

    <!-- ── Step 2: OTP ── -->
    <template v-else-if="step === 'otp'">
      <div class="flex-1 overflow-y-auto px-5 py-6">
        <Logo class="h-3 mb-6" />
        <h3 class="text-[24px] font-bold text-text-primary mb-2">Enter Verification Code</h3>
        <p class="text-sm text-text-secondary mb-6">We sent a code to {{ formattedMobileNo }}</p>
        <OtpInput v-model="otp" :error="otpError" :disabled="loading" />
        <div class="mt-4">
          <button
            v-if="canResend"
            type="button"
            class="text-primary text-sm underline"
            @click="handleResendOtp"
          >
            Resend Code
          </button>
          <p v-else class="text-text-secondary text-sm m-0">
            Resend code in {{ formattedResendTime }}
          </p>
        </div>
      </div>
      <div class="px-5 pb-6 shrink-0">
        <Button
          variant="primary"
          size="large"
          full-width
          :loading="loading"
          :disabled="!isOtpValid"
          @click="handleVerifyOtp"
        >
          Verify
        </Button>
      </div>
    </template>

    <!-- ── Step 4: Success ── -->
    <template v-else-if="step === 'complete'">
      <div class="flex-1 flex flex-col items-center justify-center px-5 py-4 text-center">
        <div class="text-5xl mb-4">✅</div>
        <h3 class="text-lg font-semibold text-text-primary m-0 mb-2">Order Placed!</h3>
        <p class="text-sm text-text-secondary m-0">
          Your order has been confirmed and reservation is active.
        </p>
      </div>
      <div class="px-5 pb-6 shrink-0">
        <Button variant="primary" size="large" full-width @click="handleViewOrders">
          View My Orders
        </Button>
      </div>
    </template>
  </div>

  <!-- Step 3: Registration — drawer on mobile, modal on desktop -->
  <UiDrawer
    v-if="windowWidth < 768"
    :model-value="step === 'register'"
    side="bottom"
    height="100dvh"
    border-radius="20px"
    @update:model-value="
      val => {
        if (!val) step = 'otp'
      }
    "
  >
    <div class="flex flex-col h-full">
      <div class="flex items-center justify-between px-5 py-4 border-b border-border">
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
          aria-label="Back"
          @click="step = 'otp'"
        >
          <Icon name="arrow-left" :size="20" weight="regular" />
        </button>
        <h2 class="text-lg font-semibold text-text-primary m-0">Complete Registration</h2>
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
          aria-label="Close"
          @click="emit('close')"
        >
          <Icon name="x" :size="20" weight="bold" />
        </button>
      </div>
      <div class="flex-1 overflow-y-auto px-5 py-5">
        <p class="text-sm text-text-secondary m-0 mb-5">
          Please provide your details to complete your order.
        </p>
        <Input
          v-model="firstName"
          label="First Name"
          placeholder="Enter first name"
          :error="errors.first_name"
          :disabled="loading"
        />
        <Input
          v-model="lastName"
          label="Last Name"
          placeholder="Enter last name"
          :error="errors.last_name"
          :disabled="loading"
          class="mt-4"
        />
        <Input
          v-model="email"
          label="Email (Optional)"
          type="email"
          placeholder="Enter email address"
          :error="errors.email"
          :disabled="loading"
          class="mt-4"
        />
      </div>
      <div class="px-5 py-4 border-t border-border bg-white">
        <Button
          variant="primary"
          size="large"
          full-width
          class="rounded-xl"
          :loading="loading"
          :disabled="!isRegistrationValid"
          @click="handleCompleteRegistration"
        >
          Complete &amp; Order
        </Button>
      </div>
    </div>
  </UiDrawer>

  <Modal
    v-else
    :model-value="step === 'register'"
    title="Complete Registration"
    :show-actions="false"
    max-width="sm"
    @update:model-value="
      val => {
        if (!val) step = 'otp'
      }
    "
  >
    <p class="text-sm text-text-secondary m-0 mb-5">
      Please provide your details to complete your order.
    </p>
    <Input
      v-model="firstName"
      label="First Name"
      placeholder="Enter first name"
      :error="errors.first_name"
      :disabled="loading"
    />
    <Input
      v-model="lastName"
      label="Last Name"
      placeholder="Enter last name"
      :error="errors.last_name"
      :disabled="loading"
      class="mt-4"
    />
    <Input
      v-model="email"
      label="Email (Optional)"
      type="email"
      placeholder="Enter email address"
      :error="errors.email"
      :disabled="loading"
      class="mt-4"
    />
    <Button
      variant="primary"
      size="large"
      full-width
      class="rounded-xl mt-4"
      :loading="loading"
      :disabled="!isRegistrationValid"
      @click="handleCompleteRegistration"
    >
      Complete &amp; Order
    </Button>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useWindowWidth } from '@/composables/core/useWindowWidth'
import { useAppFlow } from '@/composables/core/useAppFlow'
import UiDrawer from '@/components/common/UiDrawer.vue'
import Modal from '@/components/common/Modal.vue'
import Icon from '@/components/atoms/Icon.vue'
import Logo from '@/components/atoms/Logo.vue'
import MobileInput from '@/components/common/MobileInput.vue'
import OtpInput from '@/components/common/OtpInput.vue'
import Input from '@/components/common/Input.vue'
import Button from '@/components/common/Button.vue'
import { useUnifiedOrderFlow } from '@/composables/features/useUnifiedOrderFlow'
import { useMobileValidation } from '@/composables/features/useMobileValidation'
import { useOtpValidation } from '@/composables/features/useOtpValidation'
import { useResendTimer } from '@/composables/features/useResendTimer'
import { useFormValidation } from '@/composables/features/useFormValidation'
import { useNotifications } from '@/composables/core/useNotifications'

interface Props {
  tenantId: string
  tableNo: string
}

const props = defineProps<Props>()

const router = useRouter()
const { getFlowRoute, currentFlow } = useAppFlow()
const { windowWidth } = useWindowWidth()
const { showError, showSuccess } = useNotifications()

const { loading, step, sendUnifiedOtp, verifyUnifiedOtp, completeRegistrationAndOrder } =
  useUnifiedOrderFlow(props.tenantId, props.tableNo)

const {
  mobileNo,
  mobileError,
  formattedMobileNo,
  isValid: isMobileValid,
  handleMobileInputUpdate,
  validateMobile,
  setError: setMobileError,
} = useMobileValidation()

const {
  otp,
  otpError,
  isValid: isOtpValid,
  handleOtpInputUpdate,
  validateOtp,
  setError: setOtpError,
} = useOtpValidation()

const { formattedResendTime, canResend, startCooldown: startResendCooldown } = useResendTimer(120)

const { errors, validateFirstName, validateLastName, validateEmail, setError } = useFormValidation()

const firstName = ref<string>('')
const lastName = ref<string>('')
const email = ref<string>('')

const isRegistrationValid = computed(() => {
  return firstName.value.trim().length > 0 && lastName.value.trim().length > 0
})

watch(mobileNo, newValue => {
  handleMobileInputUpdate(newValue)
})

watch(otp, newValue => {
  handleOtpInputUpdate(newValue)
  if (newValue.length === 4) {
    handleVerifyOtp()
  }
})

const handleBack = (): void => {
  if (step.value === 'otp') {
    step.value = 'mobile'
  } else {
    // On mobile step, back means "return to cart" not "close everything"
    emit('back')
  }
}

const handleSendOtp = async (): Promise<void> => {
  if (!validateMobile()) return

  try {
    await sendUnifiedOtp(formattedMobileNo.value)
    startResendCooldown()
    showSuccess('Verification code sent!')
  } catch (error: unknown) {
    setMobileError(error instanceof Error ? error.message : 'Failed to send code')
  }
}

const handleVerifyOtp = async (): Promise<void> => {
  if (!validateOtp()) return

  try {
    const result = await verifyUnifiedOtp(formattedMobileNo.value, otp.value)
    if (result.success) {
      if (result.instantReservationDone) {
        showSuccess('Reservation confirmed. You can now place your order.')
        emit('instant-reservation-done', result.reservationId!)
      } else {
        showSuccess('Order placed successfully!')
        emit('order-complete', result.reservationId)
      }
    }
    // If needsRegistration, step is already set to 'register' in composable
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Verification failed'
    if (msg === 'TABLE_UNAVAILABLE') {
      emit('table-unavailable')
    } else {
      setOtpError(msg)
    }
  }
}

const handleCompleteRegistration = async (): Promise<void> => {
  if (!validateFirstName(firstName.value) || !validateLastName(lastName.value)) return
  if (email.value.trim()) {
    validateEmail(email.value)
  }

  try {
    const result = await completeRegistrationAndOrder(formattedMobileNo.value, {
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      ...(email.value.trim() ? { email: email.value.trim() } : {}),
    })

    if (result.success) {
      showSuccess('Order placed successfully!')
      emit('order-complete', result.reservationId)
    }
  } catch (error: unknown) {
    const errorMsg = error instanceof Error ? error.message : 'Registration failed'
    if (errorMsg === 'TABLE_UNAVAILABLE') {
      emit('table-unavailable')
    } else if (errorMsg.includes('first_name')) {
      setError('first_name', errorMsg)
    } else if (errorMsg.includes('last_name')) {
      setError('last_name', errorMsg)
    } else {
      showError(errorMsg)
    }
  }
}

const handleResendOtp = async (): Promise<void> => {
  if (!canResend.value) return
  await handleSendOtp()
}

const handleViewOrders = (): void => {
  router.push(getFlowRoute('/my-orders', currentFlow.value))
}

watch(firstName, () => {
  if (errors.value.first_name) {
    delete errors.value.first_name
  }
})

watch(lastName, () => {
  if (errors.value.last_name) {
    delete errors.value.last_name
  }
})

watch(email, () => {
  if (errors.value.email) {
    delete errors.value.email
  }
})

const emit = defineEmits<{
  'order-complete': [reservationId?: string]
  'instant-reservation-done': [reservationId: string]
  'table-unavailable': []
  back: []
  close: []
}>()
</script>
